import React from 'react';
import { TrendingUp, Building2, Calendar, Award } from 'lucide-react';

interface Milestone {
  year: number | string;
  title: string;
  company?: string;
  achievement?: string;
  type?: 'promotion' | 'transition' | 'achievement';
}

interface FuturePathway {
  role: string;
  timeline: string;
  probability?: number;
  salary_range?: { min: number; max: number };
  requirements?: string[];
}

interface CareerTimelineProps {
  milestones?: Milestone[];
  futurePathways?: FuturePathway[];
  title?: string;
}

const CareerTimeline: React.FC<CareerTimelineProps> = ({
  milestones = [],
  futurePathways = [],
  title = 'Career Progression & Future Pathways'
}) => {
  const formatSalary = (salary: number) => {
    return `$${(salary / 1000).toFixed(0)}k`;
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'promotion': return 'from-green-500 to-emerald-600';
      case 'transition': return 'from-blue-500 to-cyan-600';
      case 'achievement': return 'from-amber-500 to-orange-600';
      default: return 'from-purple-500 to-pink-600';
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case 'promotion': return <TrendingUp className="h-4 w-4" />;
      case 'transition': return <Building2 className="h-4 w-4" />;
      case 'achievement': return <Award className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {milestones && milestones.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold mb-6 text-gray-900">Career Milestones</h3>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500"></div>

            <div className="space-y-6">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-start gap-4 pl-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br ${getTypeColor(milestone.type)} flex items-center justify-center text-white shadow-lg z-10`}>
                    {getTypeIcon(milestone.type)}
                  </div>

                  <div className="flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{milestone.title}</h4>
                        {milestone.company && (
                          <p className="text-sm text-gray-600 mt-1">
                            <Building2 className="h-3 w-3 inline mr-1" />
                            {milestone.company}
                          </p>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">
                        {milestone.year}
                      </span>
                    </div>
                    {milestone.achievement && (
                      <p className="text-sm text-gray-700 mt-2">{milestone.achievement}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {futurePathways && futurePathways.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold mb-2 text-gray-900">Future Career Pathways</h3>
          <p className="text-sm text-gray-600 mb-6">
            Recommended career trajectories based on your skills, experience, and aspirations
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {futurePathways.map((pathway, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-5 border border-blue-200 hover:border-blue-300 transition-all hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 text-lg leading-tight pr-2">
                    {pathway.role}
                  </h4>
                  {pathway.probability && (
                    <span className="flex-shrink-0 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {pathway.probability}% fit
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">Timeline:</span>
                    <span>{pathway.timeline}</span>
                  </div>

                  {pathway.salary_range && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Salary:</span>
                      <span>
                        {formatSalary(pathway.salary_range.min)}-{formatSalary(pathway.salary_range.max)}
                      </span>
                    </div>
                  )}

                  {pathway.requirements && pathway.requirements.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">Key Requirements:</p>
                      <ul className="space-y-1">
                        {pathway.requirements.slice(0, 3).map((req, reqIndex) => (
                          <li key={reqIndex} className="text-xs text-gray-600 flex items-start gap-2">
                            <span className="text-blue-600 mt-0.5">•</span>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="text-sm font-medium text-purple-900 mb-1">Pathway Selection Guide</p>
            <p className="text-xs text-purple-700">
              Each pathway shows fit percentage based on your current skills, experience, and career aspirations.
              Higher percentages indicate stronger alignment with your profile and goals.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerTimeline;
